// Security (docs/cockpit/spec.md, Setup): how the person signs in (their Verification Methods and PIN), where they are
// signed in, with "sign out everywhere else", the External Assistants holding access with revoke (the Connections
// table), and for an Owner LAMP support's Break-glass Access to the business from the Audit Log. The Application API
// reads and ends only the person's own sessions, through the session asking. A PIN is set here after a step-up; a
// person whose consent to LAMP keeping it does not stand is asked for it first, and declining sets none (#698).
import { useCallback, useEffect, useState } from "react";
import { ApiError, type Api } from "../api.js";
import { Connections } from "../connections/Connections.js";
import { formatMoment as moment } from "../i18n/format.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import type { PinConsentGiven } from "../sign-in/api.js";
import { PinConsent } from "../sign-in/PinConsent.js";
import { useStepUp } from "../step-up/StepUp.js";
import type { SecurityView } from "./api.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** Setting the person's PIN: a `pin.change` step-up, then the consent message where their consent does not stand. */
function PinSetting({ api, onSet }: { api: Api; onSet: () => void }) {
  const w = useWords();
  const stepUp = useStepUp(api.stepUp);
  const [pin, setPin] = useState("");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const set = async (consent?: PinConsentGiven) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const outcome = await stepUp.run(() => api.security.setPin(pin, consent));
      setAsking(outcome === "consent_required");
      if (outcome === "set") {
        setPin("");
        setNotice(w.securityPinSaved);
        onSet();
      }
    } catch (error) {
      setAsking(false);
      setProblem(
        error instanceof ApiError && error.status === 422 && error.code === "weak_pin"
          ? w.securityPinWeak
          : problemOf(error, w),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void set();
        }}
      >
        <label className="field" htmlFor="security-pin">
          {w.securityPinNew}
        </label>
        <input
          id="security-pin"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={16}
          value={pin}
          onChange={(e) => setPin(e.target.value)}
        />
        {asking ? null : (
          <button type="submit" className="btn" disabled={busy || pin === ""}>
            {w.securityPinSet}
          </button>
        )}
      </form>
      {asking ? (
        <PinConsent
          busy={busy}
          decline={w.securityPinCancel}
          onConfirm={(consent) => void set(consent)}
          onDecline={() => {
            setAsking(false);
            setPin("");
            setNotice(w.securityPinNotSet);
          }}
        />
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {stepUp.dialog}
    </>
  );
}

export function Security({ api, personId }: { api: Api; personId: string }) {
  const w = useWords();
  const { language } = useLanguage();
  const [security, setSecurity] = useState<SecurityView | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.security.view().then(setSecurity, (error: unknown) => setProblem(problemOf(error, w)));
  }, [api, w]);
  useEffect(load, [load]);

  const signOutEverywhere = async () => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setNotice(w.securitySignedOutEverywhere(await api.security.signOutEverywhere()));
      load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
    }
  };

  if (!security) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.securityLoading}</p>;
  return (
    <>
      <h2>{w.securitySignIn}</h2>
      <ul>
        {security.methods.length === 0 ? <li>{w.securityNoMethod}</li> : null}
        {security.methods.map((m) => (
          <li key={m}>{w.securityMethod(m)}</li>
        ))}
        <li>{security.pin ? w.securityPin : w.securityNoPin}</li>
      </ul>
      <PinSetting api={api} onSet={load} />

      <h2>{w.securitySessions}</h2>
      <p className="muted">{w.securitySessionsIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.securitySignedIn}</th>
              <th scope="col">{w.securityDevice}</th>
              <th scope="col">{w.securityKept}</th>
              <th scope="col">{w.securityState}</th>
            </tr>
          </thead>
          <tbody>
            {security.sessions.map((s, i) => (
              <tr key={`${s.startedAt}-${String(i)}`}>
                <td>{moment(s.startedAt, language)}</td>
                <td>{s.current ? <strong>{w.securityThisDevice}</strong> : w.securityOtherDevice}</td>
                <td>{s.trusted ? w.securityTrusted : w.securityUntrusted}</td>
                <td>
                  {s.live
                    ? w.securityLive
                    : s.endedAt
                      ? w.securityEnded(moment(s.endedAt, language))
                      : w.securityExpired}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        <button type="button" className="btn" disabled={busy} onClick={() => void signOutEverywhere()}>
          {w.securitySignOutEverywhere}
        </button>
      </p>

      <h2>{w.securityAssistants}</h2>
      <Connections api={api} personId={personId} />

      {security.breakGlass ? (
        <>
          <h2>{w.securityBreakGlass}</h2>
          <p className="muted">{w.securityBreakGlassIntro}</p>
          {security.breakGlass.length === 0 ? (
            <p>{w.securityBreakGlassNone}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{w.securityBreakGlassWhen}</th>
                    <th scope="col">{w.securityBreakGlassWhat}</th>
                    <th scope="col">{w.securityBreakGlassWhy}</th>
                  </tr>
                </thead>
                <tbody>
                  {security.breakGlass.map((b, i) => (
                    <tr key={`${b.at}-${String(i)}`}>
                      <td>{moment(b.at, language)}</td>
                      <td>
                        {w.auditActions[b.action] ?? b.action}
                        {b.until && b.action === "BreakGlassOpened" ? (
                          <>
                            <br />
                            <span className="small muted">{w.securityBreakGlassUntil(moment(b.until, language))}</span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        {b.basis ? w.securityBreakGlassBasis(b.basis) : null}
                        {b.reason ? (
                          <>
                            <br />
                            <span className="small">{b.reason}</span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
